import { apiClient } from "@/lib/api-client";
import type { IdentityRequest, IdentitySuccess } from "@/lib/identity-contract";
import { recordOrganizationApiFailure } from "@/observability/browser-observability";

import type {
  CreatedEnterprise,
  CreatedInvitation,
  CreatedOrgUnit,
  CreateEnterpriseRequest,
  CreateInvitationRequest,
  CreateOrgUnitRequest,
  CreateRoleRequest,
  EnterpriseRole,
  Invitation,
  InvitationStatus,
  Member,
  OrgUnit,
  Permission,
  ResendInvitationResult,
  RevokeInvitationResult,
  Role,
  SessionResponse,
  UpdateMembershipRolesResult,
  UpdateRoleRequest,
} from "../types";

async function observeOrganizationRequest<T>(
  action: string,
  requestPath: string,
  request: () => Promise<T>
): Promise<T> {
  try {
    return await request();
  } catch (error) {
    recordOrganizationApiFailure({
      action,
      requestPath,
      error,
    });
    throw error;
  }
}

const VALID_ROLES = new Set<Role>(["admin", "member", "viewer"]);

function toRole(key: string): Role {
  if (VALID_ROLES.has(key as Role)) {
    return key as Role;
  }
  return "member";
}

const VALID_INVITATION_STATUSES = new Set<InvitationStatus>([
  "pending",
  "accepted",
  "expired",
  "revoked",
]);

function toInvitationStatus(status: string): InvitationStatus {
  if (VALID_INVITATION_STATUSES.has(status as InvitationStatus)) {
    return status as InvitationStatus;
  }
  return "pending";
}

export async function getOrgTree(orgUnitId: string): Promise<OrgUnit> {
  const res = await observeOrganizationRequest(
    "Load organization tree",
    "/identity/org-units/:id/tree",
    () => apiClient.get<IdentitySuccess<"getOrgUnitTree">>(`/identity/org-units/${orgUnitId}/tree`)
  );
  return res.data.tree;
}

export async function createEnterprise(body: CreateEnterpriseRequest): Promise<CreatedEnterprise> {
  const res = await observeOrganizationRequest("Create enterprise", "/identity/enterprises", () =>
    apiClient.post<IdentitySuccess<"createEnterprise">>("/identity/enterprises", body)
  );
  return res.data;
}

export async function createOrgUnit(body: CreateOrgUnitRequest): Promise<CreatedOrgUnit> {
  const res = await observeOrganizationRequest(
    "Create organization unit",
    "/identity/org-units",
    () => apiClient.post<IdentitySuccess<"createOrgUnit">>("/identity/org-units", body)
  );
  return res.data;
}

export async function createInvitation(
  orgUnitId: string,
  body: CreateInvitationRequest
): Promise<CreatedInvitation> {
  const res = await observeOrganizationRequest(
    "Create invitation",
    "/identity/org-units/:id/invitations",
    () =>
      apiClient.post<IdentitySuccess<"createOrgUnitInvitation">>(
        `/identity/org-units/${orgUnitId}/invitations`,
        body
      )
  );
  return res.data;
}

export async function acceptInvitation(token: string): Promise<void> {
  await observeOrganizationRequest("Accept invitation", "/identity/invitations/:token/accept", () =>
    apiClient.post<IdentitySuccess<"acceptInvitation">>(`/identity/invitations/${token}/accept`, {})
  );
}

function switchBody(orgUnitId: string): IdentityRequest<"switchSessionOrgUnit"> {
  return { org_unit_id: orgUnitId };
}

function rolesBody(roleIds: readonly string[]): IdentityRequest<"setMembershipRoles"> {
  return { role_ids: [...roleIds] };
}

export async function switchOrg(orgUnitId: string): Promise<void> {
  await observeOrganizationRequest("Switch organization context", "/identity/session/switch", () =>
    apiClient.post<IdentitySuccess<"switchSessionOrgUnit">>(
      "/identity/session/switch",
      switchBody(orgUnitId)
    )
  );
}

export async function getSession(): Promise<SessionResponse> {
  return observeOrganizationRequest("Load session", "/identity/auth/session", () =>
    apiClient.get<IdentitySuccess<"getAuthSession">>("/identity/auth/session")
  );
}

export async function getMembers(orgUnitId: string): Promise<Member[]> {
  const res = await observeOrganizationRequest(
    "Load organization members",
    "/identity/org-units/:id/members",
    () =>
      apiClient.get<IdentitySuccess<"listOrgUnitMembers">>(
        `/identity/org-units/${orgUnitId}/members`
      )
  );
  return res.data.members.map((m) => ({
    id: m.id,
    email: m.email ?? "",
    name: m.display_name ?? m.email ?? "Unknown",
    roles: m.roles.map((r) => toRole(r.role_key)),
    role_assignments: m.roles.map((r) => ({
      role_id: r.role_id,
      role_key: r.role_key,
      role_name: r.role_name,
    })),
    joined_at: m.joined_at,
  }));
}

export async function updateMembershipRoles(
  membershipId: string,
  roleIds: string[]
): Promise<UpdateMembershipRolesResult> {
  const res = await observeOrganizationRequest(
    "Update member roles",
    "/identity/memberships/:id/roles",
    () =>
      apiClient.put<IdentitySuccess<"setMembershipRoles">>(
        `/identity/memberships/${membershipId}/roles`,
        rolesBody(roleIds)
      )
  );
  return res.data;
}

export async function removeMembership(membershipId: string): Promise<void> {
  await observeOrganizationRequest("Remove member", "/identity/memberships/:id", () =>
    apiClient.del(`/identity/memberships/${membershipId}`)
  );
}

export async function revokeInvitation(invitationId: string): Promise<RevokeInvitationResult> {
  const res = await observeOrganizationRequest(
    "Revoke invitation",
    "/identity/invitations/:id/revoke",
    () =>
      apiClient.post<IdentitySuccess<"revokeOrgUnitInvitation">>(
        `/identity/invitations/${invitationId}/revoke`,
        {}
      )
  );
  return {
    id: res.data.id,
    status: toInvitationStatus(res.data.status),
  };
}

export async function resendInvitation(invitationId: string): Promise<ResendInvitationResult> {
  const res = await observeOrganizationRequest(
    "Resend invitation",
    "/identity/invitations/:id/resend",
    () =>
      apiClient.post<IdentitySuccess<"resendOrgUnitInvitation">>(
        `/identity/invitations/${invitationId}/resend`,
        {}
      )
  );
  return {
    id: res.data.id,
    token: res.data.token,
    status: toInvitationStatus(res.data.status),
    expires_at: res.data.expires_at,
  };
}

export async function getInvitations(orgUnitId: string): Promise<Invitation[]> {
  const res = await observeOrganizationRequest(
    "Load invitations",
    "/identity/org-units/:id/invitations",
    () =>
      apiClient.get<IdentitySuccess<"listOrgUnitInvitations">>(
        `/identity/org-units/${orgUnitId}/invitations`
      )
  );
  return res.data.invitations.map((inv) => ({
    id: inv.id,
    email: inv.email,
    roles: inv.roles.map((r) => toRole(r.role_key)),
    status: toInvitationStatus(inv.status),
    expires_at: inv.expires_at,
    created_at: inv.created_at,
  }));
}

export async function listRoles(enterpriseId: string): Promise<readonly EnterpriseRole[]> {
  const res = await observeOrganizationRequest(
    "Load enterprise roles",
    "/identity/enterprises/:id/roles",
    () =>
      apiClient.get<IdentitySuccess<"listEnterpriseRoles">>(
        `/identity/enterprises/${enterpriseId}/roles`
      )
  );
  return res.data.roles;
}

export async function listPermissions(): Promise<readonly Permission[]> {
  const res = await observeOrganizationRequest("Load permissions", "/identity/permissions", () =>
    apiClient.get<IdentitySuccess<"listPermissions">>("/identity/permissions")
  );
  return res.data.permissions;
}

export async function createRole(
  enterpriseId: string,
  body: CreateRoleRequest
): Promise<EnterpriseRole> {
  const res = await observeOrganizationRequest(
    "Create role",
    "/identity/enterprises/:id/roles",
    () =>
      apiClient.post<IdentitySuccess<"createEnterpriseRole">>(
        `/identity/enterprises/${enterpriseId}/roles`,
        body
      )
  );
  return res.data;
}

export async function updateRole(roleId: string, body: UpdateRoleRequest): Promise<EnterpriseRole> {
  const res = await observeOrganizationRequest("Update role", "/identity/roles/:id", () =>
    apiClient.put<IdentitySuccess<"replaceRole">>(`/identity/roles/${roleId}`, body)
  );
  return res.data;
}

export async function deleteRole(roleId: string): Promise<void> {
  await observeOrganizationRequest("Delete role", "/identity/roles/:id", () =>
    apiClient.del(`/identity/roles/${roleId}`)
  );
}
